/**
 * Persisted stat-card-visibility wiring for a grid's `options.statCards`.
 *
 * The sibling of useColumnVisibilityModel for the aggregate cards above a
 * grid: a user preference holding which cards the user has toggled off, and a
 * stable `{ model, onChange }` object so passing it into EntityGrid doesn't
 * recreate the card state on every render.
 *
 * Contains:
 * - useStatCardVisibilityModel — persisted model + memoized { model, onChange }
 */

import { useMemo } from 'react';

import useGridPreferenceKey from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useUserPreference from '@sophocles/hooks/useUserPreference';

const EMPTY_MODEL = {};

/**
 * @param {string} prefKeySuffix - fills `grid.<prefKeySuffix>.ct<customerTypeId>.statCards.v1`
 * @param {object} [defaultModel] - initial visibility model before any preference is saved
 * @returns {{ model: object, onChange: (next: object) => void }}
 */
export default function useStatCardVisibilityModel(prefKeySuffix, defaultModel = EMPTY_MODEL) {
  const prefKey = useGridPreferenceKey(prefKeySuffix, 'statCards.v1');
  const [model, setModel] = useUserPreference(prefKey, defaultModel);
  return useMemo(() => ({ model, onChange: setModel }), [model, setModel]);
}
